import React, { useState, useEffect, useContext, useMemo, useRef, useCallback } from 'react';
import { AppContext } from '../context/AppContext';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import InvoiceReport from '../components/InvoiceReport';

// ─────────────────────────────────────────────
// Small helpers
// ─────────────────────────────────────────────
const fmtCFA = (n) =>
    Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }) + ' CFA';

const statusColors = {
    Paid: 'text-emerald-400 bg-emerald-400/10 border-emerald-500/30',
    Partial: 'text-orange-400 bg-orange-400/10 border-orange-500/30',
    Unpaid: 'text-red-400 bg-red-400/10 border-red-500/30',
};

// ─────────────────────────────────────────────
// Dark overlay modal wrapper
// ─────────────────────────────────────────────
const DarkModal = ({ isOpen, onClose, title, width = 'max-w-2xl', children }) => {
    useEffect(() => {
        const handler = (e) => e.key === 'Escape' && onClose();
        if (isOpen) window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[200] flex items-center justify-center p-4"
            onClick={(e) => e.target === e.currentTarget && onClose()}>
            <div className={`bg-[#181d24] border border-gray-700/80 shadow-2xl shadow-black rounded-2xl w-full ${width} overflow-hidden flex flex-col max-h-[88vh]`}>
                {/* Header */}
                <div className="px-5 py-4 border-b border-gray-800 flex items-center justify-between bg-[#1e2530] flex-shrink-0">
                    <h3 className="text-base font-bold text-gray-100 tracking-wide">{title}</h3>
                    <button onClick={onClose} className="text-gray-500 hover:text-white transition w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-700">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
                {/* Body */}
                <div className="flex-1 overflow-y-auto">{children}</div>
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────
// Main POS Component
// ─────────────────────────────────────────────
const POS = () => {
    const { authFetch, systemSettings, user } = useContext(AppContext);
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // Core state
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('All');
    const [searchQuery, setSearchQuery] = useState('');
    const [cart, setCart] = useState([]);
    const [globalDiscount, setGlobalDiscount] = useState(0);
    const [globalDiscountPercent, setGlobalDiscountPercent] = useState(0);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [isReturnMode, setIsReturnMode] = useState(false);
    const [returnOriginalCart, setReturnOriginalCart] = useState([]); // Stores the original invoice items

    // Modal states
    const [isHoldModalOpen, setIsHoldModalOpen] = useState(false);
    const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
    const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);

    // Customer search modal state
    const [custSearch, setCustSearch] = useState('');

    // Invoice History state
    const [invoices, setInvoices] = useState([]);
    const [invoiceSearch, setInvoiceSearch] = useState('');
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [isPrintOpen, setIsPrintOpen] = useState(false);
    const [isViewOpen, setIsViewOpen] = useState(false);
    const [isPayOpen, setIsPayOpen] = useState(false);
    const [payAmount, setPayAmount] = useState('');
    const [payNote, setPayNote] = useState('');
    const getLocalDate = () => {
        const d = new Date();
        const offset = d.getTimezoneOffset() * 60000;
        return new Date(d.getTime() - offset).toISOString().split('T')[0];
    };

    const [isLoadingInvoices, setIsLoadingInvoices] = useState(false);
    const [invoiceTab, setInvoiceTab] = useState('All');
    const [startDate, setStartDate] = useState(getLocalDate());
    const [endDate, setEndDate] = useState(getLocalDate());

    // Held Orders state
    const [heldOrders, setHeldOrders] = useState([]);

    // Qty & Discount modal
    const [itemModal, setItemModal] = useState({ isOpen: false, item: null, qty: '', discount: '', discount_type: '%' });
    const [inlineEditQty, setInlineEditQty] = useState(null);
    const [inlineEditPrice, setInlineEditPrice] = useState(null);
    let clickTimeout = useRef(null);

    const searchInputRef = useRef(null);
    const custSearchRef = useRef(null);
    const invoiceSearchRef = useRef(null);

    // ── Init Data ──────────────────────────────
    const fetchData = useCallback(async () => {
        try {
            const currentDepo = systemSettings?.pos_depo_id || user?.depo_id || 1;
            const [prodRes, custRes, heldRes] = await Promise.all([
                authFetch(`/api/products?depo_id=${currentDepo}`),
                authFetch('/api/customers'),
                authFetch('/api/held_orders'),
            ]);
            if (prodRes.ok) {
                const d = await prodRes.json();
                setProducts(d);
                setCategories([...new Set(d.map(p => p.category).filter(Boolean))]);
            }
            if (custRes.ok) setCustomers(await custRes.json());
            if (heldRes.ok) setHeldOrders(await heldRes.json());
        } catch (err) {
            console.error('POS init error:', err);
        }
    }, [authFetch, systemSettings, user]);

    useEffect(() => {
        fetchData();
        if (searchInputRef.current) searchInputRef.current.focus();
    }, [fetchData]);

    useEffect(() => {
        const loadReturnInvoice = async () => {
            if (searchParams.get('mode') === 'return' && searchParams.get('invoice_id')) {
                setIsReturnMode(true);
                try {
                    // Assuming /api/invoices returns an array, we can find the specific one
                    const res = await authFetch(`/api/invoices?id=${searchParams.get('invoice_id')}`);
                    if (res.ok) {
                        const data = await res.json();
                        const invoice = Array.isArray(data) ? data.find(i => String(i.id) === searchParams.get('invoice_id')) : data;
                        if (invoice && invoice.items) {
                            const returnCart = invoice.items.map(item => ({
                                id: item.product_id,
                                name: item.product_name,
                                price: item.unit_price,
                                qty: item.quantity,
                                original_qty: item.quantity,
                                barcode: item.barcode || item.sku || ''
                            }));
                            setCart(returnCart);
                            setReturnOriginalCart(returnCart);
                        }
                    }
                } catch (e) {
                    console.error("Failed to load return invoice", e);
                }
            }
        };
        loadReturnInvoice();
    }, [authFetch, searchParams]);

    // ── Fullscreen on POS mount ────────────────
    useEffect(() => {
        const el = document.documentElement;
        const requestFS = el.requestFullscreen
            || el.webkitRequestFullscreen
            || el.mozRequestFullScreen
            || el.msRequestFullscreen;
        if (requestFS) {
            requestFS.call(el).catch(() => {/* silently ignore if blocked */ });
        }
        return () => {
            const exitFS = document.exitFullscreen
                || document.webkitExitFullscreen
                || document.mozCancelFullScreen
                || document.msExitFullscreen;
            if (exitFS && document.fullscreenElement) {
                exitFS.call(document).catch(() => { });
            }
        };
    }, []);

    // ── Focus helpers ──────────────────────────
    const focusSearch = useCallback(() => {
        setTimeout(() => searchInputRef.current?.focus(), 50);
    }, []);

    const focusCustSearch = useCallback(() => {
        setTimeout(() => custSearchRef.current?.focus(), 100);
    }, []);

    const focusInvoiceSearch = useCallback(() => {
        setTimeout(() => invoiceSearchRef.current?.focus(), 100);
    }, []);

    // (Auto-focus effect moved below fetch helpers)

    // ── Global Keyboard Shortcuts ──────────────
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'k' && (e.ctrlKey || e.metaKey)) {
                e.preventDefault();
                focusSearch();
            } else if (e.key === 'Escape') {
                if (isCustomerModalOpen) setIsCustomerModalOpen(false);
                if (isHistoryModalOpen) setIsHistoryModalOpen(false);
                if (isHoldModalOpen) setIsHoldModalOpen(false);
                if (itemModal.isOpen) setItemModal({ isOpen: false, item: null, qty: '', discount: '', discount_type: '%' });
                if (inlineEditQty !== null) setInlineEditQty(null);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [focusSearch, isCustomerModalOpen, isHistoryModalOpen, isHoldModalOpen, itemModal.isOpen, inlineEditQty]);

    // ── Fetch helpers ──────────────────────────
    const fetchHeldOrders = useCallback(async () => {
        try {
            const res = await authFetch('/api/held_orders');
            if (res.ok) setHeldOrders(await res.json());
        } catch (e) { }
    }, [authFetch]);

    const fetchInvoices = useCallback(async () => {
        setIsLoadingInvoices(true);
        try {
            let url = `/api/invoices?start_date=${startDate}&end_date=${endDate}&v=${Date.now()}`;
            const res = await authFetch(url);
            if (res.ok) {
                const data = await res.json();
                setInvoices(Array.isArray(data) ? data : []);
            }
        } catch (e) {
            console.error('Fetch invoices error:', e);
        }
        setIsLoadingInvoices(false);
    }, [authFetch, startDate, endDate]);

    // Auto-focus when modals open
    useEffect(() => { if (isCustomerModalOpen) { setCustSearch(''); focusCustSearch(); } }, [isCustomerModalOpen, focusCustSearch]);
    useEffect(() => { if (isHistoryModalOpen) { fetchInvoices(); focusInvoiceSearch(); } }, [isHistoryModalOpen, startDate, endDate, fetchInvoices, focusInvoiceSearch]);

    // ── Derived: filtered products ─────────────
    const filteredProducts = useMemo(() => {
        let result = products;
        if (selectedCategory !== 'All') result = result.filter(p => p.category === selectedCategory);
        if (searchQuery) {
            const lq = searchQuery.toLowerCase();
            result = result.filter(p =>
                p.name.toLowerCase().includes(lq) ||
                (p.barcode && p.barcode.includes(searchQuery)) ||
                (p.sku && p.sku.toLowerCase().includes(lq))
            );
        }
        return result;
    }, [products, selectedCategory, searchQuery]);

    // ── Derived: filtered customers ────────────
    const filteredCustomers = useMemo(() => {
        if (!custSearch.trim()) return customers;
        const lq = custSearch.toLowerCase();
        return customers.filter(c =>
            c.name.toLowerCase().includes(lq) ||
            (c.phone && c.phone.includes(custSearch))
        );
    }, [customers, custSearch]);

    // ── Derived: filtered invoices ─────────────
    const filteredInvoices = useMemo(() => {
        let result = invoices;

        // Filter by Tab
        if (invoiceTab !== 'All') {
            const tabLower = invoiceTab.toLowerCase();
            result = result.filter(inv => {
                const method = (inv.payment_method || 'cash').toLowerCase();
                return method === tabLower;
            });
        }

        // Filter by Search
        if (invoiceSearch.trim()) {
            const lq = invoiceSearch.toLowerCase();
            result = result.filter(inv =>
                (inv.invoice_number && inv.invoice_number.toLowerCase().includes(lq)) ||
                (inv.customer_name && inv.customer_name.toLowerCase().includes(lq))
            );
        }

        return result;
    }, [invoices, invoiceSearch, invoiceTab]);

    // Math
    const tvaRate = parseFloat(systemSettings?.default_tva || 20);
    const totalItemDiscount = cart.reduce((acc, item) => {
        let itemDisc = parseFloat(item.discount || 0);
        if (item.discount_type === '%') {
            itemDisc = (parseFloat(item.price) * item.qty) * (itemDisc / 100);
        }
        return acc + itemDisc;
    }, 0);
    const totalRemise = totalItemDiscount + parseFloat(globalDiscount || 0);

    const cartTTC = cart.reduce((acc, item) => {
        let itemDisc = parseFloat(item.discount || 0);
        if (item.discount_type === '%') {
            itemDisc = (parseFloat(item.price) * item.qty) * (itemDisc / 100);
        }
        return acc + ((parseFloat(item.price) * item.qty) - itemDisc);
    }, 0);
    const totalTTC = Math.max(0, cartTTC - globalDiscount);
    const totalHT = totalTTC / (1 + (tvaRate / 100));
    const totalTVA = totalTTC - totalHT;

    // ── Selected customer display name ─────────
    const selectedCustObj = useMemo(() => {
        if (!selectedCustomer) return null;
        return customers.find(c => String(c.id) === String(selectedCustomer));
    }, [selectedCustomer, customers]);

    const displayCustName = selectedCustObj ? selectedCustObj.name : t('Walk-in Customer');

    // ── Cart handlers ──────────────────────────
    const addToCart = useCallback((product) => {
        setCart(prev => {
            const existing = prev.find(i => i.id === product.id);
            if (existing) return prev.map(i => i.id === product.id ? { ...i, qty: i.qty + 1 } : i);
            // Strip stock-level fields from product before adding to cart to avoid quantity confusion in checkout
            const { quantity, total_quantity, stock_price, depo_name, ...cartProduct } = product;
            return [...prev, { ...cartProduct, qty: 1 }];
        });
        focusSearch();
    }, [focusSearch]);

    const updateQty = (id, newQty) => {
        if (newQty <= 0) setCart(prev => prev.filter(i => i.id !== id));
        else setCart(prev => prev.map(i => i.id === id ? { ...i, qty: newQty } : i));
    };

    const updateItemDetails = (id, details) => {
        if (details.qty <= 0) {
            setCart(prev => prev.filter(i => i.id !== id));
        } else {
            setCart(prev => prev.map(i => i.id === id ? { ...i, ...details } : i));
        }
    };

    // ── Checkout ───────────────────────────────
    const handleCheckout = async (type) => {
        if (cart.length === 0) return alert(t('Cart is empty.'));

        // Stock Validation: Block checkout if any cart item quantity exceeds available stock
        if (!isReturnMode) {
            const outOfStockItems = cart.filter(item => {
                const originalProd = products.find(p => p.id === item.id);
                const initStock = Number(originalProd?.quantity ?? item.quantity ?? 0);
                return item.qty > initStock;
            });

            if (outOfStockItems.length > 0) {
                const names = outOfStockItems.map(i => i.name).join(', ');
                return alert(t(`Checkout failed: Insufficient stock for (${names}). Cannot complete checkout with negative stock.`));
            }
        }

        setIsProcessing(true);
        
        // In Return Mode, the cart represents the FINAL kept items.
        // We must calculate the difference to send as negative quantities to the backend.
        let payloadCart = cart;
        if (isReturnMode) {
            const returnedItems = [];
            returnOriginalCart.forEach(orig => {
                const current = cart.find(c => c.id === orig.id);
                const currentQty = current ? current.qty : 0;
                const returnedQty = orig.original_qty - currentQty;
                
                if (returnedQty > 0) {
                    returnedItems.push({
                        ...orig,
                        qty: -Math.abs(returnedQty) // Backend expects negative qty for returns
                    });
                } else if (returnedQty < 0) {
                    // If they somehow increased the quantity beyond original, we could handle it as a sale, 
                    // but for simplicity in return mode, we just ignore increases beyond original.
                }
            });
            payloadCart = returnedItems;
        }

        const payload = {
            cart: payloadCart,
            tva_rate: tvaRate,
            type,
            transaction_type: isReturnMode ? 'return' : 'sale',
            original_invoice_id: isReturnMode ? searchParams.get('invoice_id') : null,
            customer_id: selectedCustomer || null,
            depo_id: systemSettings?.pos_depo_id || user?.depo_id || 1,
            discount: globalDiscount
        };
        try {
            const res = await authFetch('/api/sales', { method: 'POST', body: JSON.stringify(payload) });
            if (res.ok) {
                const data = await res.json();

                // 2. Construct Receipt Data with accurate Customer details
                const selectedCust = customers.find(c => String(c.id) === String(selectedCustomer));
                const newInvoice = {
                    id: data.invoice_id || 'TEMP',
                    invoice_number: data.invoice_id ? `INV-${String(data.invoice_id).padStart(5, '0')}` : 'NEW',
                    total_ttc: isReturnMode ? totalTTC : (data.total_ttc !== undefined ? data.total_ttc : totalTTC),
                    total_ht: isReturnMode ? totalHT : (data.total_ht !== undefined ? data.total_ht : totalHT),
                    total_tva: isReturnMode ? totalTVA : (data.total_tva !== undefined ? data.total_tva : totalTVA),
                    tva_rate: tvaRate,
                    payment_method: type,
                    amount_paid: isReturnMode ? totalTTC : (type === 'cash' || type === 'card' ? (data.total_ttc !== undefined ? data.total_ttc : totalTTC) : 0),
                    created_at: new Date().toISOString(),
                    customer_name: selectedCust ? selectedCust.name : t('Walk-in Customer'),
                    customer_phone: selectedCust ? selectedCust.phone : '',
                    discount: globalDiscount,
                    items: cart.map(i => ({
                        product_name: i.name,
                        quantity: i.qty,
                        unit_price: i.price,
                        discount: i.discount || 0,
                        discount_type: i.discount_type || '%'
                    }))
                };

                // 3. Clear Cart & Selection
                setCart([]);
                setGlobalDiscount(0);
                setGlobalDiscountPercent(0);
                setSelectedCustomer(null);

                // 4. Auto-print
                setSelectedInvoice(newInvoice);
                setIsPrintOpen(true);

                // 5. Trigger REFRESHes for all data!
                fetchData();      // Refreshes stock, customer balances, and held orders
                fetchInvoices();  // Refreshes history modal data
            } else {
                const err = await res.json();
                alert(t('Checkout failed: ') + err.message);
            }
        } catch {
            alert(t('Communication error.'));
        } finally {
            setIsProcessing(false);
            focusSearch();
        }
    };

    // ── Hold Order ─────────────────────────────
    const handleHoldOrder = async () => {
        if (cart.length === 0) return;
        try {
            const res = await authFetch('/api/held_orders', {
                method: 'POST',
                body: JSON.stringify({ cart, customer_id: selectedCustomer || null, total: totalTTC }),
            });
            if (res.ok) {
                setCart([]);
                setGlobalDiscount(0);
                setSelectedCustomer(null);
                fetchHeldOrders();
            }
        } catch {
            alert('Failed to hold order');
        }
    };

    const resumeHoldOrder = async (h) => {
        const data = JSON.parse(h.order_data);
        setCart(data.cart || []);
        setSelectedCustomer(data.customer_id || null);
        await authFetch(`/api/held_orders/${h.id}`, { method: 'DELETE' });
        fetchHeldOrders();
        setIsHoldModalOpen(false);
        focusSearch();
    };

    const deleteHoldOrder = async (id) => {
        await authFetch(`/api/held_orders/${id}`, { method: 'DELETE' });
        fetchHeldOrders();
    };

    // ── Invoice payment ────────────────────────
    const handlePay = async (e) => {
        e.preventDefault();
        const amount = parseFloat(payAmount);
        if (!amount || amount <= 0) return alert(t('Enter a valid amount.'));
        const res = await authFetch(`/api/invoices?action=pay&id=${selectedInvoice.id}`, {
            method: 'POST',
            body: JSON.stringify({ amount, note: payNote }),
        });
        if (res.ok) {
            setIsPayOpen(false);
            fetchInvoices();
        } else {
            const d = await res.json();
            alert(d.message || t('Payment failed'));
        }
    };

    // ─────────────────────────────────────────
    // RENDER
    // ─────────────────────────────────────────
    return (
        <div className="fixed inset-0 flex flex-col bg-[#0d1117] text-gray-200 font-sans select-none overflow-hidden z-10">

            {/* ── TOP NAV ────────────────────────────────── */}
            <div className="flex items-center justify-between px-6 py-3 border-b border-gray-800 bg-[#161b22] flex-shrink-0">
                <div className="flex items-center gap-6">
                    {/* Logo */}
                    <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center shadow-lg shadow-blue-500/30">
                        <span className="text-white font-black text-sm">P</span>
                    </div>
                    {/* Nav buttons — open modals instead of navigating */}
                    <nav className="flex gap-1">
                        <button
                            onClick={() => setIsCustomerModalOpen(true)}
                            className="flex items-center gap-2 text-gray-400 hover:text-blue-400 hover:bg-blue-500/10 transition-all text-sm font-semibold px-3 py-2 rounded-lg"
                        >
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                            </svg>
                            {t('Accounts')}
                        </button>
                        <button
                            onClick={() => setIsHistoryModalOpen(true)}
                            className="flex items-center gap-2 text-gray-400 hover:text-blue-400 hover:bg-blue-500/10 transition-all text-sm font-semibold px-3 py-2 rounded-lg"
                        >
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                            </svg>
                            {t('Invoice History')}
                        </button>
                    </nav>
                </div>

                {/* Mode Toggle */}
                <div className="flex bg-[#0d1117] p-1 rounded-xl border border-gray-800">
                    <button
                        onClick={() => { setIsReturnMode(false); setCart([]); setReturnOriginalCart([]); }}
                        className={`px-4 py-1.5 rounded-lg text-xs font-black uppercase tracking-widest transition ${!isReturnMode ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/20' : 'text-gray-500 hover:text-gray-300'}`}
                    >
                        {t('Sold')}
                    </button>
                    <button
                        onClick={() => { setIsReturnMode(true); setCart([]); setReturnOriginalCart([]); }}
                        className={`px-4 py-1.5 rounded-lg text-xs font-black uppercase tracking-widest transition ${isReturnMode ? 'bg-red-600 text-white shadow-lg shadow-red-600/20' : 'text-gray-500 hover:text-gray-300'}`}
                    >
                        {t('Return')}
                    </button>
                </div>

                {/* Right side controls */}
                <div className="flex items-center gap-3">
                    <button onClick={() => navigate('/sales-reports')} className="text-gray-400 hover:text-blue-400 text-sm font-bold transition px-3 py-2 rounded-lg hover:bg-blue-500/10">
                        📊 {t('Reports')}
                    </button>
                    <div className="flex items-center gap-2 bg-gray-800 rounded-full px-3 py-1.5">
                        <img src={`https://ui-avatars.com/api/?name=${user?.name || 'C'}&background=random&size=32`} alt="avatar" className="w-6 h-6 rounded-full" />
                        <span className="text-sm text-gray-200 font-semibold">{user?.name || 'Cashier'}</span>
                    </div>
                    <button
                        onClick={() => {
                            const exitFS = document.exitFullscreen
                                || document.webkitExitFullscreen
                                || document.mozCancelFullScreen
                                || document.msExitFullscreen;
                            if (exitFS && document.fullscreenElement) {
                                exitFS.call(document).catch(() => { });
                            }
                            navigate('/');
                        }}
                        className="text-gray-500 hover:text-red-400 w-9 h-9 flex items-center justify-center rounded-full hover:bg-red-400/10 transition border border-gray-700/50 hover:border-red-400/30"
                        title={t('Exit POS')}
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
            </div>

            {/* ── MAIN CONTENT ───────────────────────────── */}
            <div className="flex flex-1 overflow-hidden">

                {/* LEFT: Products */}
                <div className="flex-1 flex flex-col p-5 pr-3 bg-[#0d1117] overflow-hidden">

                    {/* Search + Categories */}
                    <div className="flex flex-col gap-3 mb-4 flex-shrink-0">
                        <div className="relative">
                            <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500 pointer-events-none">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                            </span>
                            <input
                                ref={searchInputRef}
                                type="text"
                                className="w-full bg-[#161b22] border border-gray-700 text-gray-200 rounded-full py-2.5 pl-10 pr-4 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 placeholder-gray-600 transition"
                                placeholder={t('Search Products or scan barcode...')}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                autoFocus
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && searchQuery.trim() !== '') {
                                        const query = searchQuery.trim();
                                        const exactMatch = products.find(p => p.barcode === query || p.sku === query);
                                        if (exactMatch) {
                                            addToCart(exactMatch);
                                            setSearchQuery('');
                                        } else if (filteredProducts.length === 1) {
                                            addToCart(filteredProducts[0]);
                                            setSearchQuery('');
                                        }
                                    }
                                }}
                            />
                        </div>

                        <div className="flex gap-5 overflow-x-auto border-b border-gray-800 pb-2">
                            {['All', ...categories].map(c => (
                                <button
                                    key={c}
                                    className={`whitespace-nowrap px-1 pb-1 text-sm font-semibold transition-all ${selectedCategory === c ? 'text-blue-400 border-b-2 border-blue-400' : 'text-gray-500 hover:text-gray-300'}`}
                                    onClick={() => setSelectedCategory(c)}
                                >
                                    {c}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Product Grid */}
                    <div className="flex-1 overflow-y-auto pr-1">
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                            {filteredProducts.map(p => (
                                <div
                                    key={p.id}
                                    onClick={() => addToCart(p)}
                                    className="bg-[#161b22] border border-gray-800 rounded-xl overflow-hidden hover:border-blue-500/50 hover:shadow-lg hover:shadow-blue-500/5 transition-all cursor-pointer flex flex-col h-44 group shadow-md"
                                >
                                    <div className="flex-1 flex items-center justify-center bg-[#1e2530] relative">
                                        <span className="text-gray-700 font-black text-3xl uppercase">{p.name.substring(0, 2)}</span>
                                        {cart.find(i => i.id === p.id) && (
                                            <span className="absolute top-2 right-2 w-5 h-5 bg-blue-600 rounded-full text-white text-xs font-bold flex items-center justify-center shadow">
                                                {cart.find(i => i.id === p.id).qty}
                                            </span>
                                        )}
                                    </div>
                                    <div className="p-2.5 border-t border-gray-800/60">
                                        <p className="text-xs font-bold text-gray-200 truncate leading-tight" title={p.name}>{p.name}</p>
                                        <div className="flex justify-between items-center mt-1">
                                            <p className="text-xs font-bold text-blue-400">{fmtCFA(p.price)}</p>
                                            <p className="text-[10px] font-semibold text-gray-400 bg-gray-800/80 px-1.5 py-0.5 rounded">
                                                {p.quantity || 0} {t('in stock')}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* RIGHT: Order Summary */}
                <div className="w-[600px] max-w-full bg-[#181d24] border-l border-gray-800 flex flex-col flex-shrink-0">

                    {/* Header */}
                    <div className={`px-5 py-4 flex justify-between items-center border-b border-gray-800 flex-shrink-0 ${isReturnMode ? 'bg-red-900/20' : ''}`}>
                        <h2 className={`text-base font-bold ${isReturnMode ? 'text-red-400' : 'text-gray-100'}`}>
                            {isReturnMode ? (searchParams.get('invoice_id') ? `${t('Return Mode: Invoice')} #${searchParams.get('invoice_id')}` : t('Return Items')) : t('Order Summary')}
                        </h2>
                        <div className="flex gap-2">
                            {/* Clear Cart Button for Returns */}
                            {isReturnMode && cart.length > 0 && (
                                <button
                                    onClick={() => setCart([])}
                                    className="px-2 py-1 text-[10px] font-bold bg-red-900/50 text-red-300 rounded hover:bg-red-700 hover:text-white transition"
                                >
                                    {t('Clear All')}
                                </button>
                            )}
                            {/* View Held Orders */}
                            <button
                                onClick={() => setIsHoldModalOpen(true)}
                                className="relative w-8 h-8 rounded-full bg-gray-800 hover:bg-gray-700 flex items-center justify-center text-gray-400 hover:text-white transition"
                                title={t('Held Orders')}
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                {heldOrders.length > 0 && (
                                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-orange-500 rounded-full text-white text-[9px] font-bold flex items-center justify-center">{heldOrders.length}</span>
                                )}
                            </button>
                            {/* Hold current Order */}
                            {cart.length > 0 && (
                                <button
                                    onClick={handleHoldOrder}
                                    className="w-8 h-8 rounded-full bg-blue-600/20 text-blue-400 hover:bg-blue-600 hover:text-white flex items-center justify-center transition"
                                    title={t('Hold Current Order')}
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Customer Selector — clickable box opens Customer Modal */}
                    <div
                        className="mx-4 mt-4 mb-1 p-3 bg-[#1e2530] rounded-xl border border-gray-700/50 flex items-center gap-3 cursor-pointer hover:border-blue-500/60 hover:bg-[#232b3a] transition group"
                        onClick={() => setIsCustomerModalOpen(true)}
                    >
                        <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center text-blue-400 group-hover:bg-blue-500/30 transition">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-widest">{t('Customer')}</p>
                            <p className="text-sm font-bold text-gray-200 truncate mt-0.5">{displayCustName}</p>
                        </div>
                        {selectedCustomer && (
                            <button
                                onClick={(e) => { e.stopPropagation(); setSelectedCustomer(null); }}
                                className="text-gray-600 hover:text-red-400 transition ml-1 flex-shrink-0"
                                title="Clear customer"
                            >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        )}
                        <svg className="w-4 h-4 text-gray-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                        </svg>
                    </div>

                    {/* Cart Items */}
                    <div className="flex-1 overflow-y-auto px-3 py-2">
                        {cart.length > 0 ? (
                            <div className="space-y-2">
                                {cart.map(item => {
                                    const originalProd = products.find(p => p.id === item.id);
                                    const initialStock = Number(originalProd?.quantity ?? item.quantity ?? 0);
                                    const remainingStock = initialStock - item.qty;
                                    const isOverStock = remainingStock < 0;

                                    return (
                                        <div key={item.id} className={`p-3 border rounded-xl transition-colors group flex justify-between items-center ${isOverStock ? 'bg-red-950/20 border-red-800/60' : 'border-gray-800/40 hover:bg-[#1e2530]'}`}>
                                            <div className="flex-1 pr-3 min-w-0">
                                                <p className="text-xs font-bold text-gray-200 truncate">{item.name}</p>

                                                {/* Barcode & Real-time Stock Indicator */}
                                                <div className="flex items-center gap-2 mt-1 flex-wrap">
                                                    {(item.barcode || item.code || item.sku) && (
                                                        <span className="text-[10px] font-mono text-gray-400 bg-gray-800/70 px-1.5 py-0.5 rounded border border-gray-700/50">
                                                            🏷️ {item.barcode || item.code || item.sku}
                                                        </span>
                                                    )}
                                                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${remainingStock > 0
                                                        ? 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20'
                                                        : remainingStock === 0
                                                            ? 'text-amber-400 bg-amber-500/10 border-amber-500/20'
                                                            : 'text-red-400 bg-red-500/20 border-red-500/40 font-extrabold animate-pulse'
                                                        }`}>
                                                        {remainingStock} {t('in stock')}
                                                    </span>
                                                </div>

                                                <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                                                    {/* Direct Price Edit */}
                                                    <div className="flex items-center gap-1">
                                                        <span className="text-[10px] font-semibold text-gray-500">{t('Price:')}</span>
                                                        {inlineEditPrice === item.id ? (
                                                            <input
                                                                type="number"
                                                                step="any"
                                                                min="0"
                                                                autoFocus
                                                                className="w-20 bg-[#1e2530] text-blue-400 font-bold border border-blue-500 rounded px-1.5 py-0.5 text-xs outline-none"
                                                                defaultValue={item.price}
                                                                onBlur={(e) => {
                                                                    const newPrice = parseFloat(e.target.value);
                                                                    if (!isNaN(newPrice) && newPrice >= 0) {
                                                                        updateItemDetails(item.id, { price: newPrice });
                                                                    }
                                                                    setInlineEditPrice(null);
                                                                }}
                                                                onKeyDown={(e) => {
                                                                    if (e.key === 'Enter') e.target.blur();
                                                                }}
                                                            />
                                                        ) : (
                                                            <button
                                                                type="button"
                                                                onClick={() => setInlineEditPrice(item.id)}
                                                                className="text-xs font-bold text-gray-200 hover:text-blue-400 bg-gray-800/80 px-1.5 py-0.5 rounded border border-gray-700/60 flex items-center gap-1 transition"
                                                                title={t('Click to edit price directly')}
                                                            >
                                                                {fmtCFA(item.price)}
                                                                <span className="text-[9px] text-blue-400">✏️</span>
                                                            </button>
                                                        )}
                                                    </div>

                                                    {/* Direct Item Remise Edit */}
                                                    <div className="flex items-center gap-1 bg-[#161b22] px-1.5 py-0.5 rounded border border-gray-700/60">
                                                        <span className="text-[10px] font-bold text-gray-400 uppercase">{t('Remise %:')}</span>
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            max="100"
                                                            step="any"
                                                            value={item.discount || ''}
                                                            onChange={(e) => {
                                                                const discVal = parseFloat(e.target.value) || 0;
                                                                updateItemDetails(item.id, { discount: discVal, discount_type: item.discount_type || '%' });
                                                            }}
                                                            className="w-12 bg-[#1e2530] text-orange-400 font-bold border border-gray-700/60 rounded px-1 py-0 text-center text-xs outline-none no-spinners"
                                                            placeholder="0"
                                                        />
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                const nextType = item.discount_type === '%' ? 'CFA' : '%';
                                                                updateItemDetails(item.id, { discount_type: nextType });
                                                            }}
                                                            className="text-[10px] font-black text-orange-400 hover:text-orange-300 bg-orange-500/20 px-1 py-0.5 rounded border border-orange-500/30"
                                                            title={t('Toggle % or CFA')}
                                                        >
                                                            {item.discount_type || '%'}
                                                        </button>
                                                    </div>

                                                    {/* Line Item Total */}
                                                    <div className="text-xs font-black text-emerald-400 ml-auto">
                                                        {(() => {
                                                            let disc = parseFloat(item.discount || 0);
                                                            let base = parseFloat(item.price) * item.qty;
                                                            if (item.discount_type === '%') disc = base * (disc / 100);
                                                            return fmtCFA(Math.max(0, base - disc));
                                                        })()}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Quantity Stepper */}
                                            <div className="flex items-center bg-[#161b22] rounded-lg border border-gray-700/50 flex-shrink-0">
                                                <button onClick={() => updateQty(item.id, item.qty - 1)} className="w-7 h-7 flex items-center justify-center text-gray-500 hover:text-white transition">
                                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="3">
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M20 12H4" />
                                                    </svg>
                                                </button>
                                                {inlineEditQty === item.id ? (
                                                    <input
                                                        type="number"
                                                        autoFocus
                                                        className="w-12 text-center bg-[#1e2530] text-white border border-blue-500 rounded px-1 outline-none font-bold py-0.5 text-xs"
                                                        defaultValue={item.qty}
                                                        onBlur={(e) => {
                                                            let newQty = parseFloat(e.target.value) || 1;
                                                            if (isReturnMode && item.original_qty && newQty > item.original_qty) newQty = item.original_qty;
                                                            updateQty(item.id, newQty);
                                                            setInlineEditQty(null);
                                                        }}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') e.target.blur();
                                                        }}
                                                    />
                                                ) : (
                                                    <div
                                                        className="w-10 text-center text-xs font-extrabold text-gray-100 cursor-pointer select-none flex flex-col leading-tight"
                                                        onClick={() => {
                                                            if (clickTimeout.current) clearTimeout(clickTimeout.current);
                                                            clickTimeout.current = setTimeout(() => {
                                                                setItemModal({
                                                                    isOpen: true, item,
                                                                    qty: item.qty,
                                                                    discount: item.discount || '',
                                                                    discount_type: item.discount_type || '%'
                                                                });
                                                            }, 200);
                                                        }}
                                                        onDoubleClick={() => {
                                                            if (clickTimeout.current) clearTimeout(clickTimeout.current);
                                                            setInlineEditQty(item.id);
                                                        }}
                                                    >
                                                        <span>{item.qty}</span>
                                                    </div>
                                                )}
                                                <button onClick={() => {
                                                    let newQty = item.qty + 1;
                                                    if (isReturnMode && item.original_qty && newQty > item.original_qty) newQty = item.original_qty;
                                                    updateQty(item.id, newQty);
                                                }} className="w-7 h-7 flex items-center justify-center text-gray-500 hover:text-white transition">
                                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="3">
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                                                    </svg>
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="h-full flex flex-col items-center justify-center text-gray-700 gap-3 py-16">
                                <svg className="w-12 h-12 opacity-40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                                </svg>
                                <p className="text-sm font-semibold">{t('Cart is empty')}</p>
                                <p className="text-xs text-gray-800">{t('Tap a product or scan a barcode')}</p>
                            </div>
                        )}
                    </div>

                    {/* Footer: Totals + Checkout Buttons */}
                    <div className="p-5 bg-[#0d1117] border-t border-gray-800 flex-shrink-0">
                        {/* Out of stock error banner */}
                        {!isReturnMode && cart.some(item => {
                            const originalProd = products.find(p => p.id === item.id);
                            const initStock = Number(originalProd?.quantity ?? item.quantity ?? 0);
                            return item.qty > initStock;
                        }) && (
                                <div className="mb-3 px-3 py-2 bg-red-950/70 border border-red-800/80 rounded-xl text-red-400 text-xs font-bold flex items-center gap-2 animate-pulse">
                                    <span>⚠️</span>
                                    <span>{t('Insufficient stock for item(s) in cart. Cannot checkout.')}</span>
                                </div>
                            )}

                        <div className="space-y-2 mb-5">
                            {/* Global Remise Inputs Side-by-Side */}
                            <div className="flex justify-between items-center text-xs font-black text-gray-400 uppercase tracking-widest">
                                <span className="text-orange-400">{t('Remise Global')}</span>
                                <div className="flex items-center gap-3">
                                    <div className="flex items-center gap-1">
                                        <input
                                            type="number"
                                            min="0"
                                            max="100"
                                            step="any"
                                            value={globalDiscountPercent || ''}
                                            onChange={(e) => {
                                                const pct = parseFloat(e.target.value) || 0;
                                                setGlobalDiscountPercent(pct);
                                                const calculatedCfa = (cartTTC * pct) / 100;
                                                setGlobalDiscount(calculatedCfa);
                                            }}
                                            className="w-16 bg-[#1e2530] text-orange-400 font-bold border border-orange-500/40 outline-none rounded p-1 text-right no-spinners text-xs focus:border-orange-500"
                                            placeholder="0"
                                        />
                                        <span className="text-orange-400 font-bold text-xs">%</span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <input
                                            type="number"
                                            min="0"
                                            step="any"
                                            value={globalDiscount ? Math.round(globalDiscount) : ''}
                                            onChange={(e) => {
                                                const cfa = parseFloat(e.target.value) || 0;
                                                setGlobalDiscount(cfa);
                                                const pct = cartTTC > 0 ? (cfa / cartTTC) * 100 : 0;
                                                setGlobalDiscountPercent(pct ? parseFloat(pct.toFixed(2)) : 0);
                                            }}
                                            className="w-20 bg-[#1e2530] text-blue-400 font-bold border border-gray-700/50 outline-none rounded p-1 text-right no-spinners text-xs focus:border-blue-500"
                                            placeholder="0"
                                        />
                                        <span className="text-gray-500 font-bold text-xs">CFA</span>
                                    </div>
                                </div>
                            </div>
                            <div className="flex justify-between text-xs font-black text-gray-400 uppercase tracking-widest">
                                <span>{t('Subtotal (HT)')}</span>
                                <span className="text-gray-200">{fmtCFA(totalHT)}</span>
                            </div>


                            <div className="flex justify-between text-xs font-black text-orange-500 uppercase tracking-widest pt-1 border-t border-gray-800/50">
                                <span>{t('Remise Total (Discount Total)')}</span>
                                <span>{totalRemise > 0 ? `-${fmtCFA(totalRemise)}` : fmtCFA(0)}</span>
                            </div>
                            <div className="flex justify-between text-xs font-black text-gray-400 uppercase tracking-widest">
                                <span>{t('TVA')} ({tvaRate}%)</span>
                                <span className="text-amber-500">{fmtCFA(totalTVA)}</span>
                            </div>
                            <div className="flex justify-between text-lg font-black text-gray-100 pt-2 border-t border-gray-800 uppercase tracking-widest">
                                <span>{t('Total (TTC)')}</span>
                                <span className="text-emerald-400">{fmtCFA(totalTTC)}</span>
                            </div>
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                            <button
                                onClick={() => handleCheckout('cash')}
                                disabled={cart.length === 0 || isProcessing || (!isReturnMode && cart.some(i => i.qty > Number(products.find(p => p.id === i.id)?.quantity ?? i.quantity ?? 0)))}
                                className={`${isReturnMode ? 'bg-red-600 hover:bg-red-500' : 'bg-blue-600 hover:bg-blue-500'} text-white font-bold py-3 rounded-xl transition shadow-lg text-sm disabled:opacity-40`}
                            >
                                {isReturnMode ? t('Refund Cash') : t('Cash')}
                            </button>
                            <button
                                onClick={() => handleCheckout('card')}
                                disabled={cart.length === 0 || isProcessing || (!isReturnMode && cart.some(i => i.qty > Number(products.find(p => p.id === i.id)?.quantity ?? i.quantity ?? 0)))}
                                className="bg-[#2D3342] border border-gray-700 hover:bg-gray-700 text-gray-200 font-bold py-3 rounded-xl transition text-sm disabled:opacity-40"
                            >
                                {isReturnMode ? t('Refund Card') : t('Card')}
                            </button>
                            <button
                                onClick={() => handleCheckout('credit')}
                                disabled={cart.length === 0 || isProcessing || (!selectedCustomer && !isReturnMode) || (!isReturnMode && cart.some(i => i.qty > Number(products.find(p => p.id === i.id)?.quantity ?? i.quantity ?? 0)))}
                                className={`${isReturnMode ? 'bg-orange-600 hover:bg-orange-500' : 'bg-orange-500 hover:bg-orange-400'} text-white font-bold py-3 rounded-xl transition shadow-lg text-sm disabled:opacity-50`}
                                title={!selectedCustomer && !isReturnMode ? t('Select a customer for credit') : ''}
                            >
                                {isReturnMode ? t('Returns Account') : t('Credit')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* ══════════════════════════════════════════
                MODALS
            ══════════════════════════════════════════ */}

            {/* ── 1. Customer / Account Selection Modal ── */}
            <DarkModal
                isOpen={isCustomerModalOpen}
                onClose={() => { setIsCustomerModalOpen(false); focusSearch(); }}
                title={t('Select Customer / Account')}
                width="max-w-lg"
            >
                <div className="p-4 flex flex-col gap-3">
                    {/* Search bar */}
                    <div className="relative">
                        <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500 pointer-events-none">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                        </span>
                        <input
                            ref={custSearchRef}
                            type="text"
                            className="w-full bg-[#0d1117] border border-gray-700 text-gray-200 rounded-xl py-2.5 pl-9 pr-4 focus:outline-none focus:border-blue-500 placeholder-gray-600 text-sm transition"
                            placeholder={t('Search by name or phone...')}
                            value={custSearch}
                            onChange={(e) => setCustSearch(e.target.value)}
                        />
                    </div>

                    {/* Walk-in option */}
                    <button
                        onClick={() => { setSelectedCustomer(null); setIsCustomerModalOpen(false); focusSearch(); }}
                        className={`w-full flex items-center gap-3 p-3 rounded-xl border transition text-left ${!selectedCustomer ? 'border-blue-500 bg-blue-500/10 text-blue-300' : 'border-gray-700/50 bg-[#161b22] text-gray-300 hover:border-gray-600 hover:bg-[#1e2530]'}`}
                    >
                        <div className="w-9 h-9 rounded-full bg-gray-700 flex items-center justify-center text-gray-400 flex-shrink-0">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                        </div>
                        <div>
                            <p className="font-bold text-sm">{t('Walk-in Customer')}</p>
                            <p className="text-xs text-gray-500">{t('No account — cash sale')}</p>
                        </div>
                        {!selectedCustomer && <svg className="w-4 h-4 ml-auto text-blue-400" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>}
                    </button>

                    {/* Divider */}
                    <p className="text-xs text-gray-600 font-semibold uppercase tracking-widest px-1">
                        {t('Registered Accounts')} ({filteredCustomers.length})
                    </p>

                    {/* Customer list */}
                    <div className="space-y-1 max-h-72 overflow-y-auto pr-1">
                        {filteredCustomers.length === 0 ? (
                            <p className="text-center text-gray-600 py-8 text-sm">{t('No customers found.')}</p>
                        ) : (
                            filteredCustomers.map(c => {
                                const isSelected = String(selectedCustomer) === String(c.id);
                                return (
                                    <button
                                        key={c.id}
                                        onClick={() => { setSelectedCustomer(String(c.id)); setIsCustomerModalOpen(false); focusSearch(); }}
                                        className={`w-full flex items-center gap-3 p-3 rounded-xl border transition text-left ${isSelected ? 'border-blue-500 bg-blue-500/10' : 'border-gray-700/30 bg-[#161b22] hover:border-gray-600 hover:bg-[#1e2530]'}`}
                                    >
                                        <div className={`w-9 h-9 rounded-full flex items-center justify-center font-black text-sm flex-shrink-0 ${isSelected ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300'}`}>
                                            {c.name.charAt(0).toUpperCase()}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className={`font-bold text-sm truncate ${isSelected ? 'text-blue-300' : 'text-gray-200'}`}>{c.name}</p>
                                            {c.phone && <p className="text-xs text-gray-500 mt-0.5">{c.phone}</p>}
                                        </div>
                                        {c.outstanding_balance > 0 && (
                                            <span className="text-xs font-bold text-red-400 bg-red-400/10 border border-red-500/20 rounded-full px-2 py-0.5 flex-shrink-0">
                                                {fmtCFA(c.outstanding_balance)}
                                            </span>
                                        )}
                                        {isSelected && <svg className="w-4 h-4 text-blue-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>}
                                    </button>
                                );
                            })
                        )}
                    </div>
                </div>
            </DarkModal>

            {/* ── 2. Invoice History Modal ── */}
            <DarkModal
                isOpen={isHistoryModalOpen}
                onClose={() => { setIsHistoryModalOpen(false); focusSearch(); }}
                title={t('Invoice History')}
                width="max-w-3xl"
            >
                <div className="p-4">
                    {/* Tabs + Search + Date */}
                    <div className="flex flex-col gap-4 mb-4">
                        {/* Tabs */}
                        <div className="flex gap-2 p-1 bg-[#161b22] rounded-xl border border-gray-700/50">
                            {['All', 'Cash', 'Card', 'Credit'].map(tab => (
                                <button
                                    key={tab}
                                    onClick={() => setInvoiceTab(tab)}
                                    className={`flex-1 py-2 text-xs font-bold rounded-lg transition-all ${invoiceTab === tab ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/20' : 'text-gray-500 hover:text-gray-300 hover:bg-gray-800'}`}
                                >
                                    {t(tab)}
                                </button>
                            ))}
                        </div>

                        <div className="flex gap-3">
                            {/* Search */}
                            <div className="relative flex-1">
                                <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500 pointer-events-none">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                    </svg>
                                </span>
                                <input
                                    ref={invoiceSearchRef}
                                    type="text"
                                    className="w-full bg-[#0d1117] border border-gray-700 text-gray-200 rounded-xl py-2.5 pl-9 pr-4 focus:outline-none focus:border-blue-500 placeholder-gray-600 text-sm transition"
                                    placeholder={t('Search by invoice # or customer...')}
                                    value={invoiceSearch}
                                    onChange={(e) => setInvoiceSearch(e.target.value)}
                                />
                            </div>

                            {/* Date Filter */}
                            <div className="flex items-center gap-2 bg-[#161b22] px-3 py-1 rounded-xl border border-gray-700/50">
                                <input
                                    type="date"
                                    value={startDate}
                                    onChange={e => setStartDate(e.target.value)}
                                    className="bg-transparent border-none text-xs text-gray-300 focus:ring-0 p-0"
                                />
                                <span className="text-gray-600">→</span>
                                <input
                                    type="date"
                                    value={endDate}
                                    onChange={e => setEndDate(e.target.value)}
                                    className="bg-transparent border-none text-xs text-gray-300 focus:ring-0 p-0"
                                />
                            </div>
                        </div>
                    </div>

                    {isLoadingInvoices ? (
                        <div className="text-center text-gray-500 py-16">
                            <svg className="w-6 h-6 animate-spin mx-auto mb-3 text-blue-500" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                            </svg>
                            <p className="text-sm font-semibold">{t('Loading transactions...')}</p>
                        </div>
                    ) : filteredInvoices.length === 0 ? (
                        <div className="text-center text-gray-600 py-16">
                            <p className="text-sm font-semibold">{t('No invoices found.')}</p>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {filteredInvoices.map(inv => {
                                const balance = parseFloat(inv.total_ttc) - parseFloat(inv.amount_paid);
                                return (
                                    <div key={inv.id} className="bg-[#161b22] border border-gray-800 rounded-xl p-4 flex items-center gap-4 hover:border-gray-700 transition">
                                        {/* Status badge */}
                                        <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full border flex-shrink-0 ${statusColors[inv.status] || statusColors.Unpaid}`}>
                                            {t(inv.status)}
                                        </span>
                                        {/* Details */}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <p className="font-bold text-sm text-gray-200">{inv.invoice_number || `#${inv.id}`}</p>
                                                <span className="text-gray-600 text-xs">•</span>
                                                <p className="text-xs text-gray-400 font-medium truncate">{inv.customer_name || t('Walk-in')}</p>
                                            </div>
                                            <div className="flex items-center gap-4 mt-1">
                                                <p className="text-xs text-gray-500">{inv.created_at ? new Date(inv.created_at).toLocaleString() : '—'}</p>
                                                {balance > 0 && (
                                                    <p className="text-xs text-red-400 font-semibold">{t('Owes:')} {fmtCFA(balance)}</p>
                                                )}
                                            </div>
                                        </div>
                                        {/* Amount */}
                                        <div className="text-right flex-shrink-0">
                                            <p className="text-sm font-black text-gray-100">{fmtCFA(inv.total_ttc)}</p>
                                            <p className="text-xs text-gray-600 mt-0.5">HT: {fmtCFA(inv.total_ht)}</p>
                                        </div>
                                        {/* Actions */}
                                        <div className="flex gap-2 flex-shrink-0">
                                            {inv.status !== 'Paid' && (
                                                <button
                                                    onClick={() => { setSelectedInvoice(inv); setPayAmount(''); setPayNote(''); setIsPayOpen(true); }}
                                                    className="px-3 py-1.5 bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500 hover:text-white rounded-lg text-xs font-bold border border-emerald-500/20 transition"
                                                >
                                                    {t('Pay')}
                                                </button>
                                            )}
                                            <button
                                                onClick={() => { setSelectedInvoice(inv); setIsViewOpen(true); }}
                                                className="px-3 py-1.5 bg-gray-500/10 text-gray-400 hover:bg-gray-500 hover:text-white rounded-lg text-xs font-bold border border-gray-500/20 transition"
                                            >
                                                👁 {t('View')}
                                            </button>
                                            <button
                                                onClick={() => { setSelectedInvoice(inv); setIsHistoryModalOpen(false); setIsPrintOpen(true); }}
                                                className="px-3 py-1.5 bg-blue-500/10 text-blue-400 hover:bg-blue-500 hover:text-white rounded-lg text-xs font-bold border border-blue-500/20 transition"
                                            >
                                                🖨 {t('Print')}
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </DarkModal>

            {/* ── 2.1 View Invoice Details Modal ── */}
            <DarkModal
                isOpen={isViewOpen}
                onClose={() => setIsViewOpen(false)}
                title={`${t('Invoice Details')} — ${selectedInvoice?.invoice_number || ''}`}
                width="max-w-2xl"
            >
                <div className="p-5 space-y-6">
                    {selectedInvoice && (
                        <>
                            {/* Summary Header */}
                            <div className="flex justify-between items-start">
                                <div>
                                    <p className="text-2xl font-black text-gray-100">{fmtCFA(selectedInvoice.total_ttc)}</p>
                                    <p className="text-xs text-gray-500 font-bold uppercase tracking-wider mt-1">{t('Total Amount')}</p>
                                </div>
                                <div className="text-right">
                                    <span className={`text-xs font-bold px-3 py-1 rounded-full border ${statusColors[selectedInvoice.status] || statusColors.Unpaid}`}>
                                        {t(selectedInvoice.status)}
                                    </span>
                                    <p className="text-xs text-gray-500 mt-2">{new Date(selectedInvoice.created_at).toLocaleString()}</p>
                                </div>
                            </div>

                            {/* Customer Info */}
                            <div className="bg-[#161b22] p-4 rounded-xl border border-gray-800">
                                <p className="text-[10px] text-gray-600 font-bold uppercase tracking-widest mb-1">{t('Customer')}</p>
                                <p className="text-sm font-bold text-gray-200">{selectedInvoice.customer_name || t('Walk-in Customer')}</p>
                                {selectedInvoice.customer_phone && <p className="text-xs text-gray-500 mt-0.5">{selectedInvoice.customer_phone}</p>}
                            </div>

                            {/* Items List */}
                            <div className="space-y-3">
                                <p className="text-xs text-gray-500 font-bold uppercase tracking-wider px-1">{t('Items')}</p>
                                <div className="border border-gray-800 rounded-xl overflow-hidden">
                                    <div className="w-full overflow-x-auto max-w-full">
<table className="w-full text-left text-sm">
                                        <thead className="bg-[#161b22] text-gray-500 text-[10px] uppercase font-bold">
                                            <tr>
                                                <th className="px-4 py-3">{t('Product')}</th>
                                                <th className="px-4 py-3 text-center">{t('Qty')}</th>
                                                <th className="px-4 py-3 text-right">{t('Price')}</th>
                                                <th className="px-4 py-3 text-right">{t('Total')}</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-800/50">
                                            {(selectedInvoice.items || []).map((item, idx) => (
                                                <tr key={idx} className="hover:bg-gray-800/20 transition-colors">
                                                    <td className="px-4 py-3 font-medium text-gray-200">{item.product_name}</td>
                                                    <td className="px-4 py-3 text-center text-gray-400">{item.quantity} {item.unit || ''}</td>
                                                    <td className="px-4 py-3 text-right text-gray-400">{fmtCFA(item.unit_price)}</td>
                                                    <td className="px-4 py-3 text-right font-bold text-gray-200">{fmtCFA(item.price_ttc)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
</div>
                                </div>
                            </div>

                            {/* Totals Breakdown */}
                            <div className="flex flex-col items-end gap-2 text-sm border-t border-gray-800 pt-5">
                                <div className="flex justify-between w-64">
                                    <span className="text-gray-500">{t('Subtotal (HT)')}</span>
                                    <span className="text-gray-300 font-medium">{fmtCFA(selectedInvoice.total_ht)}</span>
                                </div>
                                <div className="flex justify-between w-64">
                                    <span className="text-gray-500">{t('TVA')} ({selectedInvoice.tva_rate}%)</span>
                                    <span className="text-gray-300 font-medium">{fmtCFA(selectedInvoice.total_tva)}</span>
                                </div>
                                <div className="flex justify-between w-64 text-base font-black border-t border-gray-800 pt-2 mt-2">
                                    <span className="text-gray-100">{t('Total (TTC)')}</span>
                                    <span className="text-blue-400">{fmtCFA(selectedInvoice.total_ttc)}</span>
                                </div>
                                <div className="flex justify-between w-64 text-emerald-400 font-bold">
                                    <span className="">{t('Amount Paid')}</span>
                                    <span className="">{fmtCFA(selectedInvoice.amount_paid)}</span>
                                </div>
                                {parseFloat(selectedInvoice.total_ttc) - parseFloat(selectedInvoice.amount_paid) > 0 && (
                                    <div className="flex justify-between w-64 text-red-400 font-bold">
                                        <span className="">{t('Balance Due')}</span>
                                        <span className="">{fmtCFA(parseFloat(selectedInvoice.total_ttc) - parseFloat(selectedInvoice.amount_paid))}</span>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
                <div className="p-4 bg-[#1e2530] border-t border-gray-800 flex justify-end gap-3">
                    <button onClick={() => setIsViewOpen(false)} className="px-6 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-xl text-sm font-bold transition">
                        {t('Close')}
                    </button>
                    <button
                        onClick={() => { setIsPrintOpen(true); setIsViewOpen(false); setIsHistoryModalOpen(false); }}
                        className="px-6 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-xl text-sm font-bold shadow-lg shadow-blue-500/20 transition"
                    >
                        🖨 {t('Print')}
                    </button>
                </div>
            </DarkModal>

            {/* ── 3. Held Orders Modal ── */}
            <DarkModal
                isOpen={isHoldModalOpen}
                onClose={() => setIsHoldModalOpen(false)}
                title={t('Held Orders')}
                width="max-w-xl"
            >
                <div className="p-4">
                    {heldOrders.length === 0 ? (
                        <p className="text-center text-gray-600 py-12 font-semibold text-sm">{t('No held orders.')}</p>
                    ) : (
                        <div className="space-y-2">
                            {heldOrders.map(h => (
                                <div key={h.id} className="bg-[#161b22] border border-gray-800 p-4 rounded-xl flex items-center justify-between gap-4 hover:border-gray-700 transition">
                                    <div>
                                        <p className="text-gray-200 font-bold text-sm tracking-wide">{h.invoice_ref}</p>
                                        <p className="text-gray-500 text-xs mt-1">
                                            {new Date(h.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} &bull; {h.items_count} {t('items')}
                                        </p>
                                        {h.customer_name && <p className="text-blue-400 text-xs mt-0.5">{h.customer_name}</p>}
                                    </div>
                                    <p className="text-gray-300 font-bold text-sm mr-auto ml-4">{fmtCFA(h.total_ttc)}</p>
                                    <div className="flex gap-2">
                                        <button onClick={() => deleteHoldOrder(h.id)} className="px-3 py-1.5 bg-red-500/10 text-red-400 hover:bg-red-500 hover:text-white rounded-lg text-xs font-bold border border-red-500/20 transition">
                                            {t('Discard')}
                                        </button>
                                        <button onClick={() => resumeHoldOrder(h)} className="px-4 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-xs font-bold shadow transition">
                                            {t('Return to Invoice')}
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </DarkModal>

            {/* ── 4. Item Details Editor (Qty & Discount) ── */}
            {itemModal.isOpen && (
                <div className="fixed inset-0 bg-black/50 z-[300] flex justify-end" onClick={() => setItemModal({ isOpen: false, item: null, qty: '', discount: '', discount_type: '%' })}>
                    <div className="bg-[#1e2330] border-l border-gray-800 shadow-2xl w-80 h-full flex flex-col p-6 overflow-y-auto"
                        onClick={e => e.stopPropagation()}>
                        <h4 className="text-gray-100 font-bold mb-1">{t('Edit Line Item')}</h4>
                        <p className="text-blue-400 text-xs font-semibold mb-5 truncate">{itemModal.item?.name}</p>

                        <div className="space-y-5 mb-6">
                            <div>
                                <label className="text-[10px] uppercase font-bold tracking-widest text-gray-500 mb-2 block">{t('Quantity')}</label>
                                <input
                                    type="number"
                                    autoFocus
                                    min="0"
                                    className="bg-[#161b22] border border-gray-700 text-gray-100 text-2xl font-black text-center rounded-xl py-3 px-2 w-full focus:outline-none focus:border-blue-500 transition"
                                    value={itemModal.qty}
                                    onChange={e => setItemModal({ ...itemModal, qty: e.target.value })}
                                    onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
                                />
                            </div>

                            <div>
                                <label className="text-[10px] uppercase font-bold tracking-widest text-gray-500 mb-2 block">{t('Discount')}</label>
                                <div className="flex bg-[#161b22] border border-gray-700 rounded-xl overflow-hidden focus-within:border-blue-500 transition">
                                    <input
                                        type="number"
                                        min="0"
                                        className="bg-transparent text-gray-100 text-xl font-bold text-center py-3 px-2 flex-1 focus:outline-none"
                                        value={itemModal.discount}
                                        onChange={e => setItemModal({ ...itemModal, discount: e.target.value })}
                                        placeholder="0"
                                    />
                                    <select
                                        className="bg-gray-800 text-gray-300 font-bold text-sm border-l border-gray-700 px-3 outline-none cursor-pointer"
                                        value={itemModal.discount_type}
                                        onChange={e => setItemModal({ ...itemModal, discount_type: e.target.value })}
                                    >
                                        <option value="%">%</option>
                                        <option value="amount">CFA</option>
                                    </select>
                                </div>
                                <p className="text-[10px] text-gray-600 mt-1.5">
                                    {itemModal.discount_type === '%'
                                        ? t('Percentage off the line total')
                                        : t('Fixed CFA amount off')}
                                </p>
                            </div>
                        </div>

                        <div className="flex gap-3 mt-auto">
                            <button className="flex-1 bg-gray-700 hover:bg-gray-600 text-gray-200 py-3 rounded-xl font-bold transition text-sm"
                                onClick={() => setItemModal({ isOpen: false, item: null, qty: '', discount: '', discount_type: '%' })}>
                                {t('Cancel')}
                            </button>
                            <button className="flex-[1.5] bg-blue-600 hover:bg-blue-500 text-white py-3 rounded-xl font-bold transition shadow-lg text-sm"
                                onClick={() => {
                                    updateItemDetails(itemModal.item.id, {
                                        qty: parseFloat(itemModal.qty) || 0,
                                        discount: parseFloat(itemModal.discount) || 0,
                                        discount_type: itemModal.discount_type
                                    });
                                    setItemModal({ isOpen: false, item: null, qty: '', discount: '', discount_type: '%' });
                                    focusSearch();
                                }}>
                                {t('Save Changes')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── 5. Payment Modal (from Invoice History) ── */}
            {isPayOpen && selectedInvoice && (
                <div className="fixed inset-0 bg-black/70 z-[400] flex items-center justify-center p-4">
                    <div className="bg-[#181d24] border border-gray-700 rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
                        <div className="px-5 py-4 border-b border-gray-800 flex items-center justify-between bg-[#1e2530]">
                            <h3 className="font-bold text-gray-100 text-sm">{t('Record Payment')} — {selectedInvoice.invoice_number}</h3>
                            <button onClick={() => { setIsPayOpen(false); setIsHistoryModalOpen(true); }} className="text-gray-500 hover:text-white transition">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                        </div>
                        <form onSubmit={handlePay} className="p-5 space-y-4">
                            <div className="bg-[#161b22] rounded-xl p-4 border border-gray-800 space-y-2 text-sm">
                                <div className="flex justify-between"><span className="text-gray-500">{t('Customer')}</span><span className="font-semibold text-gray-200">{selectedInvoice.customer_name || t('Walk-in')}</span></div>
                                <div className="flex justify-between"><span className="text-gray-500">{t('Total')}</span><span className="font-bold text-gray-100">{fmtCFA(selectedInvoice.total_ttc)}</span></div>
                                <div className="flex justify-between"><span className="text-gray-500">{t('Paid')}</span><span className="font-semibold text-emerald-400">{fmtCFA(selectedInvoice.amount_paid)}</span></div>
                                <div className="flex justify-between border-t border-gray-700 pt-2"><span className="text-gray-500">{t('Remaining')}</span><span className="font-black text-red-400">{fmtCFA(parseFloat(selectedInvoice.total_ttc) - parseFloat(selectedInvoice.amount_paid))}</span></div>
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-widest">{t('Amount Received')}</label>
                                <input required type="number" min="0.01" step="0.01" autoFocus
                                    className="w-full bg-[#161b22] border border-gray-700 text-gray-100 text-2xl font-black text-center rounded-xl py-4 focus:outline-none focus:border-blue-500"
                                    value={payAmount} onChange={e => setPayAmount(e.target.value)} />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-400 mb-1 uppercase tracking-widest">{t('Note (optional)')}</label>
                                <input type="text" className="w-full bg-[#161b22] border border-gray-700 text-gray-200 rounded-xl p-3 focus:outline-none focus:border-blue-500 text-sm"
                                    value={payNote} onChange={e => setPayNote(e.target.value)} placeholder={t('Cash, bank transfer...')} />
                            </div>
                            <button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-500 text-white py-3 rounded-xl font-bold shadow-lg transition">
                                ✓ {t('Record Payment')}
                            </button>
                        </form>
                    </div>
                </div>
            )}

            {/* ── 6. Print Receipt ── */}
            {isPrintOpen && selectedInvoice && (
                <InvoiceReport
                    invoice={selectedInvoice}
                    onClose={() => setIsPrintOpen(false)}
                    isPOS={true}
                    autoPrint={true}
                />
            )}
        </div>
    );
};

export default POS;
